import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { ExpenseInput } from "../../types";
import { formatMoney } from "../../lib/money-math";

/**
 * The bank-markup fields. With auto-calculate on, typing the charged amount works out the markup
 * percentage and typing the percentage works out the amount, both against the amount as it was
 * when editing began — a stable "before the bank added its markup" figure, instead of whatever is
 * currently typed.
 */
export function useMarkupCalc(input: ExpenseInput, setInput: Dispatch<SetStateAction<ExpenseInput>>) {
  const [trackMarkup, setTrackMarkup] = useState(!!input.markup);
  const [markupAutoCalc, setMarkupAutoCalc] = useState(false);
  const markupBaseline = useRef(input.amount);
  function changeAmount(value: string) {
    setInput((v) => {
      const next = { ...v, amount: value };
      const base = Number(markupBaseline.current);
      if (markupAutoCalc && base > 0 && value !== "") {
        const pct = ((Number(value) - base) / base) * 100;
        if (Number.isFinite(pct) && pct >= 0)
          next.markup = String(Math.round(pct * 100) / 100);
      }
      return next;
    });
  }
  function changeMarkup(value: string) {
    setInput((v) => {
      const next = { ...v, markup: value };
      const base = Number(markupBaseline.current);
      if (markupAutoCalc && base > 0 && value !== "") {
        const amount = base * (1 + Number(value) / 100);
        if (Number.isFinite(amount)) next.amount = formatMoney(amount);
      }
      return next;
    });
  }
  return { trackMarkup, setTrackMarkup, markupAutoCalc, setMarkupAutoCalc, markupBaseline, changeAmount, changeMarkup };
}
